import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Stack } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useAdminDataGuard } from "../hooks/useAdminDataGuard";
import * as accountServices from "../../contexts/services/admin/accountServices";
import AccountDetails from "../components/accounts/Details/AccountDetails";

const AdminAccountDetailPage = () => {
  const { u_id } = useParams();
  const navigate = useNavigate();
  const canAccessAdminData = useAdminDataGuard();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const safeUid = useMemo(() => String(u_id || ""), [u_id]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!safeUid) return;
      setLoading(true);
      setError(null);
      try {
        const res = await accountServices.getAccountDetail(safeUid);
        const payload = res?.data ?? res;
        if (!alive) return;
        setData(payload);
      } catch (e) {
        if (!alive) return;
        setError(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            "Failed to load account detail",
        );
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [safeUid]);

  if (!canAccessAdminData) {
    return <Alert severity='error'>Admin token validation failed.</Alert>;
  }

  return (
    <Stack spacing={2} sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Button
        variant='text'
        onClick={() => navigate("/internal/accounts/list")}
        sx={{ alignSelf: "flex-start", textTransform: "none" }}
      >
        ← Back to accounts
      </Button>

      {error ? <Alert severity='error'>{error}</Alert> : null}

      <AccountDetails loading={loading} data={data} u_id={safeUid} />
    </Stack>
  );
};

export default AdminAccountDetailPage;
